import React, { useEffect, useState } from 'react';
import { APIProvider, Map, AdvancedMarker, Pin, useMap, useMapsLibrary, InfoWindow, useAdvancedMarkerRef } from '@vis.gl/react-google-maps';
import { MapPin, X, Navigation2, Store, AlertCircle, DollarSign, Loader2, Utensils, Star, RotateCcw, Target, Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import { Language, GroceryItem, DeficiencyAlert } from '../types';
import { fetchWrapper, safeJsonResponse } from '../lib/api';
import { toast } from 'sonner';

const API_KEY =
  process.env.GOOGLE_MAPS_PLATFORM_KEY ||
  (import.meta as any).env?.VITE_GOOGLE_MAPS_PLATFORM_KEY ||
  (globalThis as any).GOOGLE_MAPS_PLATFORM_KEY ||
  '';
const hasValidKey = Boolean(API_KEY) && API_KEY !== 'YOUR_API_KEY';

interface PriceEstimateItem {
  name: string;
  estimatedPrice: string;
  benefit?: string;
  isDeficitCloser?: boolean;
}

interface DeficitQuickFilter {
  id: string;
  label: string;
  query: string;
  nutrient: string;
}

function getDeficiencyQuickFilters(deficiencies: DeficiencyAlert[] = [], lang: Language): DeficitQuickFilter[] {
  if (!deficiencies || deficiencies.length === 0) return [];
  return deficiencies.map(d => {
    const key = (d.nutrientKey || '').toLowerCase();
    const name = d.nutrientName || '';
    if (key.includes('omega') || key.includes('омега')) {
      return {
        id: 'omega',
        label: lang === 'ua' ? '🐟 Омега-3 (Риба/Суші)' : '🐟 Omega-3 (Fish/Sushi)',
        query: lang === 'ua' ? 'суші риба морепродукти' : 'sushi seafood fish',
        nutrient: name
      };
    }
    if (key.includes('protein') || key.includes('білок') || key.includes('iron') || key.includes('залізо')) {
      return {
        id: 'protein_iron',
        label: lang === 'ua' ? '🥩 Білок & Залізо (Мʼясо/Гриль)' : '🥩 Protein & Iron (Meat/Grill)',
        query: lang === 'ua' ? 'стейк гриль мʼясо шашлик' : 'steak grill meat',
        nutrient: name
      };
    }
    if (key.includes('fiber') || key.includes('клітковина') || key.includes('vitaminc') || key.includes('вітамін c')) {
      return {
        id: 'fiber_vitc',
        label: lang === 'ua' ? '🥗 Клітковина (Боули/Салати)' : '🥗 Fiber & Vitamins (Bowls/Salads)',
        query: lang === 'ua' ? 'боули салат здорова їжа' : 'healthy bowls salads',
        nutrient: name
      };
    }
    if (key.includes('calcium') || key.includes('кальцій') || key.includes('vitamind') || key.includes('вітамін d')) {
      return {
        id: 'calcium_vitd',
        label: lang === 'ua' ? '🧀 Кальцій & D (Сир/Кулінарія)' : '🧀 Calcium & Vit D (Bistro/Dairy)',
        query: lang === 'ua' ? 'кулінарія сир піца' : 'bistro bakery cheese',
        nutrient: name
      };
    }
    const foodKeywords = d.recommendedFoods?.slice(0, 2).join(' ') || '';
    return {
      id: key || name,
      label: `🎯 ${name}`,
      query: foodKeywords ? `${foodKeywords} ${name}` : `${name} їжа`,
      nutrient: name
    };
  });
}

interface StoreLocatorProps {
  isOpen?: boolean;
  onClose: () => void;
  lang: Language;
  theme: 'light' | 'dark';
  initialQuery?: string;
  searchQuery?: string;
  items?: GroceryItem[];
  mode?: 'restaurant' | 'grocery';
  deficiencies?: DeficiencyAlert[];
}

function MarkerWithInfoWindow({ 
  place, 
  isSelected,
  onSelect,
  onEstimate,
  lang,
  isRestaurant
}: {
  place: google.maps.places.Place;
  userLocation: google.maps.LatLngLiteral | null;
  isSelected?: boolean;
  onSelect?: (place: google.maps.places.Place) => void;
  onEstimate?: (place: google.maps.places.Place) => void;
  lang: Language;
  isRestaurant?: boolean;
}) {
  const [markerRef, marker] = useAdvancedMarkerRef();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isSelected) {
      setOpen(true);
    }
  }, [isSelected]);

  if (!place.location) return null;

  return (
    <>
      <AdvancedMarker 
        ref={markerRef} 
        position={place.location} 
        onClick={() => {
          setOpen(true);
          onSelect?.(place);
        }}
      >
        <Pin 
          background={isSelected ? "#F59E0B" : (isRestaurant ? "#F97316" : "#10B981")} 
          glyphColor="#000" 
          borderColor={isSelected ? "#D97706" : (isRestaurant ? "#EA580C" : "#059669")} 
        />
      </AdvancedMarker>
      {open && (
        <InfoWindow 
          anchor={marker} 
          onCloseClick={() => {
            setOpen(false);
          }}
        >
          <div className="p-2 max-w-[220px] font-sans">
            <h3 className="font-extrabold text-sm text-zinc-900 mb-1">{place.displayName}</h3>
            <p className="text-xs text-zinc-600 mb-2">{place.formattedAddress}</p>
            {place.rating && (
              <p className="text-xs font-bold text-amber-500 mb-2 flex items-center gap-1">
                <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> {place.rating} ({place.userRatingCount || 0})
              </p>
            )}
            <div className="flex flex-col gap-1.5 pt-2 border-t border-zinc-200">
              <button
                type="button"
                onClick={() => {
                  onSelect?.(place);
                  onEstimate?.(place);
                }}
                className="w-full bg-[#10B981] hover:bg-emerald-500 text-black font-black uppercase tracking-widest text-[9px] py-2 px-2.5 rounded-xl flex items-center justify-center gap-1.5 transition-all shadow-sm cursor-pointer"
              >
                <DollarSign className="w-3.5 h-3.5" />
                {lang === 'ua' ? 'Оцінити вартість' : 'Estimate Prices'}
              </button>
              <a 
                href={`https://www.google.com/maps/dir/?api=1&destination=${typeof place.location.lat === 'function' ? place.location.lat() : (place.location as any).lat},${typeof place.location.lng === 'function' ? place.location.lng() : (place.location as any).lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1 text-[10px] uppercase font-black tracking-widest text-zinc-600 hover:text-zinc-900 pt-1"
              >
                <Navigation2 className="w-3 h-3 text-[#10B981]" /> {lang === 'ua' ? 'Маршрут' : 'Get Directions'}
              </a>
            </div>
          </div>
        </InfoWindow>
      )}
    </>
  );
}

function MapLogic({ 
  userLocation, 
  defaultQuery,
  customQuery,
  isRestaurant,
  selectedPlace,
  onSelectPlace,
  onEstimatePlace,
  lang
}: { 
  userLocation: google.maps.LatLngLiteral | null; 
  defaultQuery: string;
  customQuery: string;
  isRestaurant: boolean;
  selectedPlace?: google.maps.places.Place | null;
  onSelectPlace?: (place: google.maps.places.Place) => void;
  onEstimatePlace?: (place: google.maps.places.Place) => void;
  lang: Language;
}) {
  const placesLib = useMapsLibrary('places');
  const map = useMap();
  const [places, setPlaces] = useState<google.maps.places.Place[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Allow clicking directly on any basemap POI (e.g. Sushi Zoom, Жито Гастродім, cafes)
  useEffect(() => {
    if (!map || !placesLib?.Place) return;

    const clickListener = map.addListener('click', async (e: google.maps.IconMouseEvent) => {
      if (e.placeId) {
        if (typeof e.stop === 'function') e.stop();
        try {
          const place = new placesLib.Place({ id: e.placeId });
          await place.fetchFields({
            fields: ['displayName', 'location', 'formattedAddress', 'rating', 'userRatingCount', 'types']
          });
          if (place.location) {
            onSelectPlace?.(place);
            setPlaces(prev => {
              if (prev.some(p => p.id === place.id || p.displayName === place.displayName)) {
                return prev;
              }
              return [place, ...prev];
            });
          }
        } catch (err) {
          console.warn("Failed to fetch clicked POI details:", err);
        }
      }
    });

    return () => {
      google.maps.event.removeListener(clickListener);
    };
  }, [map, placesLib, onSelectPlace]);

  // Main search effect: searches nearby establishments and deficit-closing venues
  useEffect(() => {
    if (!placesLib?.Place || !map || !userLocation) return;
    
    let isMounted = true;
    setIsSearching(true);

    const performSearch = async () => {
      try {
        let results: google.maps.places.Place[] = [];
        const effectiveQuery = (customQuery || defaultQuery || '').trim();

        if (effectiveQuery) {
          // Targeted keyword search (custom search or nutritional deficit filter)
          const searchResponse = await placesLib.Place.searchByText({
            textQuery: effectiveQuery,
            fields: ['displayName', 'location', 'formattedAddress', 'rating', 'userRatingCount', 'types'],
            locationBias: map.getCenter() || userLocation,
            maxResultCount: 20,
          });
          results = searchResponse.places || [];
        } else {
          // Comprehensive discovery: attempt searchNearby first with all dining / takeaway / cafe types
          if (typeof placesLib.Place.searchNearby === 'function') {
            try {
              const nearbyTypes = isRestaurant
                ? ['restaurant', 'cafe', 'meal_takeaway', 'fast_food_restaurant', 'bakery', 'bar']
                : ['supermarket', 'grocery_store'];

              const nearbyResponse = await placesLib.Place.searchNearby({
                fields: ['displayName', 'location', 'formattedAddress', 'rating', 'userRatingCount', 'types'],
                locationRestriction: {
                  center: map.getCenter() || userLocation,
                  radius: 2500, // 2.5 km radius around user
                },
                includedTypes: nearbyTypes,
                maxResultCount: 20,
              });
              results = nearbyResponse.places || [];
            } catch (nearbyErr) {
              console.warn("searchNearby call fallback to searchByText:", nearbyErr);
            }
          }

          // Fallback to broad multi-keyword text search if searchNearby returned empty or errored
          if (!results || results.length === 0) {
            const fallbackQuery = isRestaurant
              ? (lang === 'ua' ? 'їжа ресторани кафе суші заклади харчування' : 'food restaurants cafes takeaway sushi')
              : (lang === 'ua' ? 'супермаркет продукти магазин' : 'supermarket grocery store');

            const fallbackResponse = await placesLib.Place.searchByText({
              textQuery: fallbackQuery,
              fields: ['displayName', 'location', 'formattedAddress', 'rating', 'userRatingCount', 'types'],
              locationBias: map.getCenter() || userLocation,
              maxResultCount: 20,
            });
            results = fallbackResponse.places || [];
          }
        }

        if (isMounted) {
          setPlaces(results);

          // Smoothly fit bounds to include user and found venues
          if (results.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            bounds.extend(userLocation);
            results.forEach(p => {
              if (p.location) bounds.extend(p.location);
            });
            map.fitBounds(bounds, 50);
          }
        }
      } catch (err) {
        console.error("Places API search error:", err);
      } finally {
        if (isMounted) {
          setIsSearching(false);
        }
      }
    };

    performSearch();

    return () => {
      isMounted = false;
    };
  }, [placesLib, map, userLocation, defaultQuery, customQuery, isRestaurant, lang]);

  return (
    <>
      {userLocation && (
        <AdvancedMarker position={userLocation} zIndex={50}>
          <div className="w-4 h-4 bg-blue-500 border-2 border-white rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]" />
        </AdvancedMarker>
      )}
      {places.map((p, i) => (
        <MarkerWithInfoWindow 
          key={p.id || i} 
          place={p} 
          userLocation={userLocation}
          isSelected={selectedPlace?.displayName === p.displayName}
          onSelect={onSelectPlace}
          onEstimate={onEstimatePlace}
          lang={lang}
          isRestaurant={isRestaurant}
        />
      ))}
      
      {isSearching && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/80 backdrop-blur-md text-white text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full flex items-center gap-2 z-20 shadow-lg">
          <div className="w-3 h-3 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin" />
          {lang === 'ua' ? 'Пошук закладів...' : 'Searching venues...'}
        </div>
      )}
    </>
  );
}

export const StoreLocator: React.FC<StoreLocatorProps> = ({ 
  onClose, 
  lang, 
  theme, 
  initialQuery = '', 
  searchQuery = '', 
  items = [],
  mode,
  deficiencies = []
}) => {
  const isRestaurant = mode === 'restaurant' || /ресторан|кафе|restaurant|cafe|bar|бар|dinner|lunch|dining|eating/i.test(searchQuery || initialQuery || '');
  
  // Top-level state hoisting
  const [userLocation, setUserLocation] = useState<google.maps.LatLngLiteral | null>(null);
  const [, setLocationError] = useState<string | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.Place | null>(null);
  const [estimatedPrices, setEstimatedPrices] = useState<PriceEstimateItem[] | null>(null);
  const [estimatedVenueName, setEstimatedVenueName] = useState<string>('');
  const [isEstimating, setIsEstimating] = useState(false);
  const [activeDeficitQuery, setActiveDeficitQuery] = useState<string>('');

  const deficitFilters = getDeficiencyQuickFilters(deficiencies, lang);

  const handleEstimatePrices = async (targetPlace?: google.maps.places.Place | null) => {
    const placeToUse = targetPlace !== undefined ? targetPlace : selectedPlace;
    setIsEstimating(true);

    const venueName = placeToUse?.displayName || (isRestaurant ? (lang === 'ua' ? 'Ресторани поруч' : 'Nearby Restaurants') : (searchQuery || ''));
    setEstimatedVenueName(venueName);

    try {
      let prompt = '';
      let cacheKey = '';

      if (isRestaurant || placeToUse) {
        const activeDefNames = deficiencies.map(d => d.nutrientName).filter(Boolean);
        cacheKey = `price_est_venue_${venueName}_${activeDefNames.join('_')}_${lang}`;
        const venueAddress = placeToUse?.formattedAddress ? ` (${placeToUse.formattedAddress})` : '';
        
        const deficitPromptSection = activeDefNames.length > 0 
          ? `\nNUTRITIONAL DEFICITS TO CLOSE: The user has active daily deficits in: ${activeDefNames.join(', ')}.
Specifically include 1-2 dishes at this venue that directly help close these deficits (e.g. salmon/sushi for Omega-3, beef steak for Iron/Protein, grain bowl for Fiber).
Add a "benefit" field stating which nutrient it replenishes (e.g. "Багатий на Омега-3" or "High in Iron & Protein") and "isDeficitCloser": true.`
          : '';

        prompt = `You are an expert culinary and nutrition concierge.
Estimate current typical menu prices and average check for the dining venue: "${venueName}"${venueAddress}.
${deficitPromptSection}

Return a JSON array of 4-6 objects with:
- "name": string (dish name)
- "estimatedPrice": string (e.g. "₴250 – ₴420" or "$18 – $28")
- "benefit": optional string (e.g. "Джерело Омега-3")
- "isDeficitCloser": optional boolean (true if it helps close a deficit)

Include:
1. ${lang === 'ua' ? 'Середній чек (на людину)' : 'Average check per person'}
2. ${activeDefNames.length > 0 ? (lang === 'ua' ? `Страва для закриття дефіциту (${activeDefNames.slice(0, 2).join(', ')})` : `Dish to close deficit (${activeDefNames.slice(0, 2).join(', ')})`) : (lang === 'ua' ? 'Основна фірмова страва' : 'Signature / Main Course')}
3. ${lang === 'ua' ? 'Салат або здорова закуска' : 'Healthy Salad or Starter'}
4. ${lang === 'ua' ? 'Кава або напій' : 'Coffee or Beverage'}
5. ${lang === 'ua' ? 'Десерт або перекус' : 'Dessert or Snack'}

Use realistic prices. Since the query is Ukrainian or in Ukraine, use Ukrainian hryvnia (₴ UAH).
Return ONLY a valid JSON array of objects, with NO markdown formatting, no other text.`;
      } else {
        const rawItems = searchQuery ? [{ name: searchQuery }] : items;
        const itemsToEstimate = (rawItems.length > 0 ? rawItems : [{ name: lang === 'ua' ? 'Базовий продуктовий кошик' : 'Basic grocery basket' }])
          .slice(0, 15)
          .map(i => ({ name: String(i.name || '').trim().substring(0, 60) }))
          .filter(i => i.name.length > 0);

        cacheKey = `price_est_items_${itemsToEstimate.map(i => i.name).sort().join('|')}_${lang}`;
        prompt = `You are a helpful AI that estimates current average grocery prices. 
The user is looking to buy: ${itemsToEstimate.map(i => i.name).join(', ')}.
Provide a JSON array of objects with "name" and "estimatedPrice" (as a string with currency symbol, e.g. "$4.50" or "₴150"). Keep it realistic. If the item names are in Ukrainian, use ₴ (UAH), otherwise default to local currency (e.g., $). Return ONLY a valid JSON array, nothing else.`;
      }

      // Check session cache before firing AI request
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setEstimatedPrices(parsed);
            setIsEstimating(false);
            return;
          }
        } catch {
          // ignore cache parse error
        }
      }

      const response = await fetchWrapper('/api/ai/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          params: {
            model: "gemini-3.5-flash-lite",
            contents: [{ role: "user", parts: [{ text: prompt }] }],
            config: { 
              responseMimeType: "application/json",
              maxOutputTokens: 600
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Server returned status ${response.status}`);
      }

      const data = await safeJsonResponse(response);

      let text = data.result?.text;
      if (!text && data.result?.candidates?.[0]?.content?.parts) {
        const parts = data.result.candidates[0].content.parts;
        const nonThought = parts.filter((p: any) => !p.thought);
        if (nonThought.length > 0) {
          text = nonThought.map((p: any) => p.text || '').join('');
        } else if (parts[0]?.text) {
          text = parts[0].text;
        }
      }
      if (!text && typeof data.result === 'string') {
        text = data.result;
      }

      if (text) {
        const match = text.match(/\[[\s\S]*\]/);
        const jsonStr = match ? match[0] : text.replace(/```json/g, '').replace(/```/g, '').trim();
        const parsed = JSON.parse(jsonStr);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setEstimatedPrices(parsed);
          sessionStorage.setItem(cacheKey, JSON.stringify(parsed));
          return;
        }
      }
      throw new Error("Unable to parse price estimation response");
    } catch (err) {
      console.warn('AI price estimation fallback activated:', err);
      const activeDefNames = deficiencies.map(d => d.nutrientName).filter(Boolean);
      const fallback: PriceEstimateItem[] = isRestaurant || placeToUse ? [
        { name: lang === 'ua' ? 'Середній чек (на людину)' : 'Average Check (per person)', estimatedPrice: '₴250 – ₴450' },
        ...(activeDefNames.length > 0 ? [{
          name: lang === 'ua' ? `Страва для закриття дефіциту (${activeDefNames[0]})` : `Nutrient-Rich Dish (${activeDefNames[0]})`,
          estimatedPrice: '₴220 – ₴360',
          benefit: lang === 'ua' ? `Закриває дефіцит: ${activeDefNames[0]}` : `Closes deficit: ${activeDefNames[0]}`,
          isDeficitCloser: true
        }] : []),
        { name: lang === 'ua' ? 'Основна фірмова страва' : 'Main Course', estimatedPrice: '₴180 – ₴320' },
        { name: lang === 'ua' ? 'Салат або здорова закуска' : 'Salad or Starter', estimatedPrice: '₴120 – ₴210' },
        { name: lang === 'ua' ? 'Кава або напій' : 'Coffee or Beverage', estimatedPrice: '₴55 – ₴95' },
      ] : [
        { name: lang === 'ua' ? 'Базовий продуктовий кошик' : 'Basic Grocery Basket', estimatedPrice: '₴350 – ₴600' }
      ];
      setEstimatedPrices(fallback);
      toast.info(lang === 'ua' ? 'Орієнтовні ціни сформовано' : 'Estimated price benchmarks generated');
    } finally {
      setIsEstimating(false);
    }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error("Geolocation error:", error);
          setLocationError(lang === 'ua' ? 'Не вдалося визначити локацію' : 'Failed to get location');
          setUserLocation({ lat: 50.4501, lng: 30.5234 }); // Default to Kyiv center
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    } else {
      setLocationError(lang === 'ua' ? 'Геолокація не підтримується' : 'Geolocation not supported');
      setUserLocation({ lat: 50.4501, lng: 30.5234 });
    }
  }, [lang]);

  if (!hasValidKey) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="bg-[var(--bg)] border border-[var(--card-border)] rounded-3xl p-6 max-w-md w-full shadow-2xl relative">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-[var(--text)] transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="bg-red-500/10 w-12 h-12 rounded-full flex items-center justify-center mb-4 text-red-500">
            <AlertCircle className="w-6 h-6" />
          </div>
          
          <h2 className="text-xl font-black uppercase tracking-widest text-[var(--text)] mb-2">
            Google Maps API Key Required
          </h2>
          <div className="text-sm font-medium text-zinc-500 space-y-3 mt-4">
            <p><strong>Step 1:</strong> Get an API Key from Google Cloud Console</p>
            <p><strong>Step 2:</strong> Add your key as a secret in AI Studio:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Open <strong>Settings</strong> (⚙️ gear icon, <strong>top-right corner</strong>)</li>
              <li>Select <strong>Secrets</strong></li>
              <li>Type <code className="bg-zinc-100 dark:bg-zinc-800 px-1 rounded">GOOGLE_MAPS_PLATFORM_KEY</code> as the secret name, press <strong>Enter</strong></li>
              <li>Paste your API key as the value, press <strong>Enter</strong></li>
            </ul>
            <p>The app rebuilds automatically after you add the secret.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 100 }}
      className="fixed inset-0 z-[100] bg-[var(--bg)] flex flex-col"
    >
      {/* Top Header */}
      <div className="flex-none flex items-center justify-between p-4 border-b border-[var(--card-border)] bg-[var(--card-bg)] shadow-sm z-30 relative">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isRestaurant ? 'bg-amber-500/10 text-amber-500' : 'bg-emerald-500/10 text-[#10B981]'}`}>
            {isRestaurant ? <Utensils className="w-5 h-5" /> : <Store className="w-5 h-5" />}
          </div>
          <div>
            <h2 className="text-sm font-black uppercase tracking-widest text-[var(--text)]">
              {isRestaurant 
                ? (lang === 'ua' ? 'Заклади харчування поруч' : 'Nearby Dining & Food')
                : (lang === 'ua' ? 'Найближчі магазини' : 'Nearby Stores')
              }
            </h2>
            {activeDeficitQuery ? (
              <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                {lang === 'ua' ? 'Фільтр: ' : 'Filter: '}{activeDeficitQuery}
              </p>
            ) : (searchQuery && !/ресторан|кафе|restaurant|cafe/i.test(searchQuery)) ? (
              <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                {lang === 'ua' ? 'Пошук: ' : 'Search: '}{searchQuery}
              </p>
            ) : null}
          </div>
        </div>
        
        <button 
          onClick={onClose}
          className="p-2 bg-zinc-100 dark:bg-zinc-800 rounded-full text-zinc-500 hover:text-[var(--text)] transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Deficit Quick Filter Bar (When user has nutritional deficits to close) */}
      {isRestaurant && deficitFilters.length > 0 && (
        <div className="flex-none px-4 py-2.5 bg-[var(--card-bg)]/90 backdrop-blur-md border-b border-[var(--card-border)] overflow-x-auto no-scrollbar flex items-center gap-2 z-20">
          <span className="text-[10px] font-black uppercase tracking-wider text-amber-500 flex items-center gap-1 shrink-0">
            <Target className="w-3.5 h-3.5" />
            {lang === 'ua' ? 'Закрити дефіцити:' : 'Close deficits:'}
          </span>
          <button
            type="button"
            onClick={() => setActiveDeficitQuery('')}
            className={`px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider transition-all shrink-0 cursor-pointer ${
              !activeDeficitQuery 
                ? 'bg-[#10B981] text-black shadow-sm font-black' 
                : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 hover:text-[var(--text)]'
            }`}
          >
            {lang === 'ua' ? 'Всі поруч' : 'All Nearby'}
          </button>
          {deficitFilters.map((df) => {
            const isSelected = activeDeficitQuery === df.query;
            return (
              <button
                key={df.id}
                type="button"
                onClick={() => {
                  setActiveDeficitQuery(isSelected ? '' : df.query);
                  setSelectedPlace(null);
                  setEstimatedPrices(null);
                }}
                className={`px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider transition-all shrink-0 flex items-center gap-1.5 cursor-pointer ${
                  isSelected
                    ? 'bg-amber-500 text-black shadow-sm font-black'
                    : 'bg-amber-500/10 dark:bg-amber-500/15 text-amber-500 hover:bg-amber-500/20 border border-amber-500/30'
                }`}
              >
                <span>{df.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Map View */}
      <div className="flex-1 relative">
        {!userLocation ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500">
            <div className="w-8 h-8 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-xs font-black uppercase tracking-widest">
              {lang === 'ua' ? 'Визначення локації...' : 'Locating...'}
            </p>
          </div>
        ) : (
          <APIProvider apiKey={API_KEY} version="weekly">
            <Map
              defaultCenter={userLocation}
              defaultZoom={15}
              mapId="STORE_LOCATOR_MAP_ID"
              internalUsageAttributionIds={['gmp_mcp_codeassist_v1_aistudio']}
              style={{ width: '100%', height: '100%' }}
              disableDefaultUI={true}
              zoomControl={true}
            >
              <MapLogic 
                userLocation={userLocation} 
                defaultQuery={searchQuery}
                customQuery={activeDeficitQuery}
                isRestaurant={isRestaurant}
                selectedPlace={selectedPlace}
                onSelectPlace={(place) => {
                  setSelectedPlace(place);
                  setEstimatedPrices(null);
                }}
                onEstimatePlace={(place) => {
                  setSelectedPlace(place);
                  handleEstimatePrices(place);
                }}
                lang={lang}
              />
            </Map>
          </APIProvider>
        )}
        
        {/* Info panel at bottom */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[92%] max-w-md bg-[var(--card-bg)] p-4 rounded-3xl border border-[var(--card-border)] shadow-2xl flex flex-col gap-3 max-h-[48vh] overflow-hidden z-20">
           {/* Selected place badge if user tapped a spot */}
           {selectedPlace && (
             <div className="flex items-center justify-between bg-amber-500/10 dark:bg-amber-500/15 border border-amber-500/30 px-3 py-2 rounded-2xl">
               <div className="flex items-center gap-2 truncate">
                 <MapPin className="w-4 h-4 text-amber-500 shrink-0" />
                 <div className="truncate">
                   <p className="text-xs font-black text-[var(--text)] truncate">{selectedPlace.displayName}</p>
                   {selectedPlace.formattedAddress && (
                     <p className="text-[10px] text-zinc-500 truncate">{selectedPlace.formattedAddress}</p>
                   )}
                 </div>
               </div>
               <button 
                 onClick={() => {
                   setSelectedPlace(null);
                   setEstimatedPrices(null);
                 }}
                 className="text-zinc-400 hover:text-[var(--text)] p-1 shrink-0 cursor-pointer"
                 title={lang === 'ua' ? 'Скинути вибір' : 'Clear selection'}
               >
                 <X className="w-4 h-4" />
               </button>
             </div>
           )}

           <div className="flex items-start gap-3 shrink-0">
              <div className="bg-amber-500/10 p-2 rounded-xl text-amber-500 shrink-0">
                 <AlertCircle className="w-5 h-5" />
              </div>
              <div>
                 <p className="text-[10px] font-bold text-zinc-600 dark:text-zinc-400 leading-tight">
                    {isRestaurant
                      ? (lang === 'ua' 
                          ? 'ШІ оцінює меню, середній чек та страви, що допомагають закрити ваші щоденні дефіцити поживних речовин. Тапніть будь-який заклад на мапі!'
                          : 'AI estimates menus, average checks, and deficit-closing dishes for your daily nutrition. Tap any venue on the map!')
                      : (lang === 'ua' 
                          ? 'Ціни на окремі продукти не доступні в Google Maps. Натомість ми показуємо найближчі супермаркети та ринки.'
                          : 'Individual item prices are not available via Google Maps. Showing nearby supermarkets and grocery stores instead.')
                    }
                 </p>
              </div>
           </div>

           {/* Estimated Prices Feature Action */}
           {(!estimatedPrices && !isEstimating) && (
              <button 
                onClick={() => handleEstimatePrices()}
                className="mt-1 w-full bg-[#10B981] hover:bg-emerald-500 text-black font-black uppercase tracking-widest text-[10px] py-3 px-4 rounded-2xl flex items-center justify-center gap-2 transition-all transform hover:scale-[1.01] active:scale-[0.98] shadow-md cursor-pointer"
              >
                <DollarSign className="w-4 h-4" />
                <span className="truncate">
                  {selectedPlace 
                    ? (lang === 'ua' ? `ОЦІНИТИ ВАРТІСТЬ: ${selectedPlace.displayName}` : `ESTIMATE PRICES: ${selectedPlace.displayName}`)
                    : (isRestaurant 
                        ? (lang === 'ua' ? 'ОЦІНИТИ ВАРТІСТЬ СТРАВ' : 'ESTIMATE DISH PRICES')
                        : (lang === 'ua' ? 'ОЦІНИТИ ВАРТІСТЬ ПРОДУКТІВ' : 'ESTIMATE GROCERY PRICES')
                      )
                  }
                </span>
              </button>
           )}

           {isEstimating && (
             <div className="flex flex-col items-center justify-center p-3 text-[#10B981]">
               <Loader2 className="w-6 h-6 animate-spin mb-2" />
               <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                 {lang === 'ua' ? 'ШІ аналізує ціни та корисні страви...' : 'AI estimating prices & nutrient dishes...'}
               </p>
             </div>
           )}

           {estimatedPrices && (
             <div className="mt-1 overflow-y-auto no-scrollbar pb-2 border-t border-[var(--card-border)] pt-2.5">
               <div className="flex items-center justify-between mb-2.5">
                 <h3 className="text-xs font-black uppercase tracking-widest text-[var(--text)] flex items-center gap-1.5 truncate">
                   <DollarSign className="w-4 h-4 text-[#10B981] shrink-0" />
                   <span className="truncate">
                     {estimatedVenueName 
                       ? (lang === 'ua' ? `Ціни: ${estimatedVenueName}` : `Prices: ${estimatedVenueName}`)
                       : (lang === 'ua' ? 'Орієнтовна вартість' : 'Estimated Prices')
                     }
                   </span>
                 </h3>
                 <button
                   onClick={() => setEstimatedPrices(null)}
                   className="text-[9px] font-bold uppercase tracking-widest text-zinc-400 hover:text-[var(--text)] transition-colors shrink-0 ml-2 cursor-pointer flex items-center gap-1"
                   title={lang === 'ua' ? 'Очистити' : 'Reset'}
                 >
                   <RotateCcw className="w-3 h-3" />
                   {lang === 'ua' ? 'Скинути' : 'Reset'}
                 </button>
               </div>
               <div className="space-y-1.5">
                 {estimatedPrices.map((item, idx) => (
                   <div 
                     key={idx} 
                     className={`flex items-center justify-between p-2.5 rounded-xl text-xs font-bold border transition-colors ${
                       item.isDeficitCloser 
                         ? 'bg-amber-500/10 dark:bg-amber-500/15 border-amber-500/30' 
                         : 'bg-zinc-50 dark:bg-zinc-800/50 border-[var(--card-border)]/50'
                     }`}
                   >
                     <div className="flex flex-col min-w-0 mr-2">
                       <span className="text-[var(--text)] truncate">{item.name}</span>
                       {item.benefit && (
                         <span className="text-[9px] font-black text-amber-500 flex items-center gap-1 mt-0.5">
                           <Sparkles className="w-2.5 h-2.5 shrink-0" /> {item.benefit}
                         </span>
                       )}
                     </div>
                     <span className="text-[#10B981] whitespace-nowrap font-black shrink-0">{item.estimatedPrice}</span>
                   </div>
                 ))}
               </div>
             </div>
           )}
        </div>
      </div>
    </motion.div>
  );
};
